import { Link, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { ArrowRight, Menu, X } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { supabase } from "@/integrations/supabase/client";
import { TOPICS } from "@/lib/topics";
import { TOPICS_DE } from "@/lib/topics.de";
import { INDUSTRIES } from "@/lib/industries";

function useAuthed() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setAuthed(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(!!session);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);
  return authed;
}

export function useHydratedTranslation() {
  const { t: liveT, i18n } = useTranslation();
  const [hydrated, setHydrated] = useState(false);
  const englishT = useMemo(() => i18n.getFixedT("en"), [i18n]);

  useEffect(() => setHydrated(true), []);

  return hydrated ? liveT : englishT;
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <div className="font-mono-eyebrow">{children}</div>;
}

export function SectionHeader({
  eyebrow,
  title,
  sub,
  align = "left",
}: {
  eyebrow?: string;
  title: ReactNode;
  sub?: ReactNode;
  align?: "left" | "center";
}) {
  return (
    <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="mt-3 font-serif text-[40px] sm:text-[56px] leading-[1.02] tracking-[-0.025em]">
        {title}
      </h2>
      {sub && (
        <p
          className={`mt-5 text-[17px] leading-[1.55] text-muted-foreground max-w-xl ${align === "center" ? "mx-auto" : ""}`}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

const NAV_LINKS = [
  { to: "/platform", key: "nav.platform" },
  { to: "/connect", key: "nav.connect" },
  { to: "/pricing", key: "nav.pricing" },
  { to: "/docs", key: "nav.docs" },
  { to: "/blog", key: "nav.blog" },
  { to: "/about", key: "nav.about" },
  { to: "/changelog", key: "nav.changelog" },
] as const;

export function Nav() {
  const t = useHydratedTranslation();
  const authed = useAuthed();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Mobile menu: the desktop nav is hidden below md — without this drawer,
  // phone visitors could not reach any marketing page. Closes on route change.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  async function handleSignOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    router.invalidate();
    navigate({ to: "/", replace: true });
  }

  return (
    <header className="sticky top-0 z-50 border-b border-border/70 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto grid h-14 max-w-6xl grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-5 sm:flex sm:justify-between sm:px-6">
        <div className="flex min-w-0 items-center gap-2">
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="md:hidden -ml-1 p-2 rounded-lg text-muted-foreground hover:text-foreground transition"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <Logo size={19} />
        </div>
        <nav className="hidden md:flex items-center gap-7 text-[13.5px] text-muted-foreground">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="hover:text-foreground transition"
              activeProps={{ className: "text-foreground" }}
            >
              {t(l.key)}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-1.5 shrink-0">
          <LanguageSwitcher />
          {authed ? (
            <>
              <button
                type="button"
                onClick={handleSignOut}
                className="hidden sm:inline-flex text-[13.5px] text-muted-foreground hover:text-foreground px-3 py-1.5 transition"
              >
                {t("app.shell.signOut")}
              </button>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-1 rounded-full bg-foreground px-3.5 py-1.5 text-[13px] font-medium text-background hover:opacity-90 transition"
              >
                {t("app.shell.nav.dashboard")}
                <ArrowRight className="h-3 w-3" strokeWidth={2} />
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/auth"
                className="hidden sm:inline-flex text-[13.5px] text-muted-foreground hover:text-foreground px-3 py-1.5 transition"
              >
                {t("nav.signIn")}
              </Link>
              <Link
                to="/auth"
                className="inline-flex items-center gap-1 rounded-full bg-foreground px-3.5 py-1.5 text-[13px] font-medium text-background hover:opacity-90 transition"
              >
                <span>{t("nav.requestAccess")}</span>
                <ArrowRight className="h-3 w-3" strokeWidth={2} />
              </Link>
            </>
          )}
        </div>
      </div>
      {open && (
        <nav className="md:hidden border-t border-border/70 bg-background/95 backdrop-blur-xl">
          <div className="mx-auto max-w-6xl px-5 py-3 flex flex-col">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="py-3 text-[15px] text-muted-foreground hover:text-foreground border-b border-border/50 last:border-0 transition"
                activeProps={{ className: "text-foreground" }}
              >
                {t(l.key)}
              </Link>
            ))}
            {!authed && (
              <Link
                to="/auth"
                className="py-3 text-[15px] text-muted-foreground hover:text-foreground transition sm:hidden"
              >
                {t("nav.signIn")}
              </Link>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}

type FooterLink = { labelKey: string; to: string; hash?: string };

const FOOTER_COLS: { titleKey: string; links: FooterLink[] }[] = [
  {
    titleKey: "footer.col.product",
    links: [
      { labelKey: "footer.links.platform", to: "/platform" },
      { labelKey: "footer.links.twinCheck", to: "/twin-check" },
      { labelKey: "footer.links.pricing", to: "/pricing" },
      { labelKey: "footer.links.useCases", to: "/use-cases" },
      { labelKey: "footer.links.industries", to: "/industries" },
      { labelKey: "footer.links.network", to: "/network" },
      { labelKey: "footer.links.connect", to: "/connect" },
      { labelKey: "footer.links.roadmap", to: "/roadmap" },
      { labelKey: "footer.links.changelog", to: "/changelog" },
    ],
  },
  {
    titleKey: "footer.col.resources",
    links: [
      { labelKey: "footer.links.topics", to: "/topics" },
      { labelKey: "footer.links.blog", to: "/blog" },
      { labelKey: "footer.links.docs", to: "/docs" },
      { labelKey: "footer.links.faq", to: "/faq" },
      { labelKey: "footer.links.support", to: "/support" },
      { labelKey: "footer.links.compare", to: "/compare" },
    ],
  },
  {
    titleKey: "footer.col.company",
    links: [
      { labelKey: "footer.links.about", to: "/about" },
      { labelKey: "footer.links.customers", to: "/customers" },
      { labelKey: "footer.links.careers", to: "/careers" },
      { labelKey: "footer.links.invest", to: "/invest" },
      { labelKey: "footer.links.contact", to: "/contact" },
      { labelKey: "footer.links.nuvatr", to: "/nuvatr" },
    ],
  },
  {
    titleKey: "footer.col.legal",
    links: [
      { labelKey: "footer.links.impressum", to: "/impressum" },
      { labelKey: "footer.links.privacy", to: "/privacy" },
      { labelKey: "footer.links.terms", to: "/terms" },
      { labelKey: "footer.links.dpa", to: "/dpa" },
      { labelKey: "footer.links.security", to: "/security" },
      { labelKey: "footer.links.subprocessors", to: "/subprocessors" },
    ],
  },
];

export function Footer() {
  const t = useHydratedTranslation();
  return (
    <footer className="border-t border-border py-14">
      <div className="mx-auto max-w-6xl px-6 grid grid-cols-2 md:grid-cols-6 gap-10 text-[13px]">
        <div className="col-span-2">
          <div className="flex items-center gap-2.5">
            <Logo size={19} />
          </div>
          <p className="mt-4 text-muted-foreground max-w-xs leading-relaxed">
            {t("footer.tagline")}
          </p>
          <p className="mt-4 text-[12px] text-muted-foreground/80 leading-relaxed">
            {t("footer.ownedBy", { company: "Ruznic Marketing", owner: "Ajdin Ruznic" })
              .split("Ruznic Marketing")
              .map((part, i, arr) =>
                i < arr.length - 1 ? (
                  <span key={i}>
                    {part}
                    <a
                      href="https://ruznic-marketing.com"
                      className="underline hover:text-foreground transition"
                    >
                      Ruznic Marketing
                    </a>
                  </span>
                ) : (
                  <span key={i}>{part}</span>
                ),
              )}
            <br />
            Leimgrubstrasse 5, 8340 Hinwil, Switzerland.
          </p>
          <div className="mt-5">
            <LanguageSwitcher align="left" />
          </div>
        </div>
        {FOOTER_COLS.map((col) => (
          <div key={col.titleKey}>
            <div className="font-mono-eyebrow">{t(col.titleKey)}</div>
            <ul className="mt-3 space-y-2 text-muted-foreground">
              {col.links.map((l) => (
                <li key={l.labelKey}>
                  <Link to={l.to} hash={l.hash} className="hover:text-foreground transition">
                    {t(l.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Every landing page, reachable from every page. Four columns cannot hold
          twenty entries without becoming unreadable, so the full directory sits
          below them — which also gives each page an internal link from the whole
          site rather than only from its hub. */}
      <div className="mx-auto max-w-6xl px-6 mt-12 pt-8 border-t border-border space-y-6">
        <div>
          <div className="font-mono-eyebrow">{t("footer.dir.topics")}</div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-[13px] text-muted-foreground">
            {TOPICS.map((topic) => (
              <Link
                key={topic.slug}
                to="/topics/$slug"
                params={{ slug: topic.slug }}
                className="hover:text-foreground transition"
              >
                {topic.name}
              </Link>
            ))}
          </div>
        </div>
        <div>
          {/* The German pages are real URLs with their own content, so they get
              their own footer row rather than hiding behind a language switch. */}
          <div className="font-mono-eyebrow">Themen (Deutsch)</div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-[13px] text-muted-foreground">
            {TOPICS_DE.map((topic) => (
              <Link
                key={topic.slug}
                to="/de/themen/$slug"
                params={{ slug: topic.slug }}
                className="hover:text-foreground transition"
              >
                {topic.name}
              </Link>
            ))}
          </div>
        </div>
        <div>
          <div className="font-mono-eyebrow">{t("footer.dir.industries")}</div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-[13px] text-muted-foreground">
            {INDUSTRIES.map((industry) => (
              <Link
                key={industry.slug}
                to="/industries/$slug"
                params={{ slug: industry.slug }}
                className="hover:text-foreground transition"
              >
                {industry.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 mt-10 pt-6 border-t border-border flex flex-col md:flex-row justify-between gap-3 text-[12px] text-muted-foreground">
        <div>{t("footer.rights", { year: 2026 })}</div>
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-foreground/70 animate-pulse" />{" "}
          {t("footer.buildingInPublic")}
        </div>
      </div>
    </footer>
  );
}

export function MarketingLayout({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Nav />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

export function PageHero({
  eyebrow,
  title,
  sub,
}: {
  eyebrow: string;
  title: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <section className="border-b border-border">
      <div className="mx-auto max-w-6xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="mt-4 font-serif text-[48px] sm:text-[80px] leading-[0.98] tracking-[-0.035em] max-w-3xl">
          {title}
        </h1>
        {sub && (
          <p className="mt-6 max-w-xl text-[17px] leading-[1.55] text-muted-foreground">{sub}</p>
        )}
      </div>
    </section>
  );
}

/**
 * I18n-aware page hero. Routes pass a translation namespace key
 * (e.g. "pages.pricing"); the hero pulls eyebrow/title/sub itself.
 */
export function I18nPageHero({ tKey }: { tKey: string }) {
  const t = useHydratedTranslation();
  return (
    <PageHero eyebrow={t(`${tKey}.eyebrow`)} title={t(`${tKey}.title`)} sub={t(`${tKey}.sub`)} />
  );
}

export function Prose({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto max-w-3xl px-6 py-20 text-[15.5px] leading-[1.7] text-muted-foreground [&>h2]:font-serif [&>h2]:text-[28px] [&>h2]:text-foreground [&>h2]:mt-12 [&>h2]:mb-4 [&>h2]:tracking-[-0.025em] [&>h2:first-child]:mt-0 [&>h3]:text-foreground [&>h3]:font-medium [&>h3]:text-[17px] [&>h3]:mt-8 [&>h3]:mb-2 [&>p]:mb-5 [&>ul]:mb-5 [&>ul]:list-disc [&>ul]:pl-6 [&>ul>li]:mb-2 [&>a]:underline [&>a]:text-foreground hover:[&>a]:opacity-70">
      {children}
    </div>
  );
}
